import { useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { regionsWithArtwork } from "../data/regions";

export default function RegionViewer({ regionId, imageIndex, onClose, onNavigate, onExplore }) {
  const regions = regionsWithArtwork();
  const rIdx = regions.findIndex((r) => r.id === regionId);
  const region = rIdx >= 0 ? regions[rIdx] : null;
  const total = region ? region.images.length : 0;

  const goPrev = useCallback(() => {
    if (!region) return;
    if (imageIndex > 0) {
      onNavigate(region.id, imageIndex - 1);
    } else {
      const prev = regions[(rIdx - 1 + regions.length) % regions.length];
      onNavigate(prev.id, prev.images.length - 1);
    }
  }, [imageIndex, region, regions, rIdx, onNavigate]);

  const goNext = useCallback(() => {
    if (!region) return;
    if (imageIndex < total - 1) {
      onNavigate(region.id, imageIndex + 1);
    } else {
      const next = regions[(rIdx + 1) % regions.length];
      onNavigate(next.id, 0);
    }
  }, [imageIndex, total, region, regions, rIdx, onNavigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext, onClose]);

  if (!region) return null;

  return (
    <motion.div
      data-testid="region-viewer"
      className="fixed inset-0 z-[70] flex flex-col"
      style={{ background: "rgba(10, 9, 7, 0.97)" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.7 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
    >
      {/* viewer chrome */}
      <div className="flex items-center justify-between px-5 sm:px-8 py-4 z-10">
        <button
          data-testid="viewer-return"
          onClick={onClose}
          className="flex items-center gap-2 font-nav text-[10px] tracking-[0.3em] uppercase text-[#B8A795] hover:text-[#C5A059] transition-colors duration-500"
        >
          <X size={14} strokeWidth={1.5} />
          Return to Bharat
        </button>
        <button
          data-testid="viewer-explore"
          onClick={onExplore}
          className="font-nav text-[10px] tracking-[0.3em] uppercase text-[#857565] hover:text-[#C5A059] transition-colors duration-500"
        >
          Another Region
        </button>
      </div>

      {/* artwork */}
      <motion.div
        className="flex-1 flex items-center justify-center px-4 sm:px-16 min-h-0"
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.25}
        onDragEnd={(e, info) => {
          if (info.offset.x < -80) goNext();
          else if (info.offset.x > 80) goPrev();
        }}
      >
        <AnimatePresence mode="wait">
          <motion.figure
            key={`${region.id}-${imageIndex}`}
            initial={{ opacity: 0, scale: 0.985 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.01 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="manuscript-frame max-h-full"
          >
            <img
              src={region.images[imageIndex]}
              alt={`${region.name} artwork`}
              data-testid="viewer-image"
              className="max-h-[68dvh] max-w-full w-auto object-contain select-none"
              draggable={false}
            />
          </motion.figure>
        </AnimatePresence>
      </motion.div>

      {/* caption + controls */}
      <div className="px-5 sm:px-8 pb-6 pt-2 z-10">
        <div className="flex items-end justify-between gap-6 max-w-5xl mx-auto w-full">
          <button
            data-testid="viewer-prev"
            onClick={goPrev}
            aria-label="Previous"
            className="group flex items-center gap-2 text-[#857565] hover:text-[#C5A059] transition-colors duration-500 pb-1"
          >
            <ChevronLeft size={16} strokeWidth={1.25} />
            <span className="hidden sm:inline font-nav text-[10px] tracking-[0.3em] uppercase">Prev</span>
          </button>

          <div className="text-center min-w-0" data-testid="viewer-caption">
            <p className="font-nav text-[9px] tracking-[0.45em] uppercase text-[#857565]">
              {region.ut ? "Union Territory of Bharat" : `${region.zone} Bharat`}{total > 1 ? ` · ${imageIndex + 1} of ${total}` : ""}
            </p>
            <h2 className="font-display uppercase tracking-[0.12em] text-[#E8DCC4] text-xl sm:text-3xl mt-2 leading-snug">
              {region.name}
            </h2>
            <p className="font-serif-body italic text-[#B8A795] text-sm sm:text-base mt-2 max-w-lg mx-auto">
              {region.description}
            </p>
          </div>

          <button
            data-testid="viewer-next"
            onClick={goNext}
            aria-label="Next"
            className="group flex items-center gap-2 text-[#857565] hover:text-[#C5A059] transition-colors duration-500 pb-1"
          >
            <span className="hidden sm:inline font-nav text-[10px] tracking-[0.3em] uppercase">Next</span>
            <ChevronRight size={16} strokeWidth={1.25} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
